import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DollarSign } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import RentalTariffsTab from './RentalTariffsTab';
import TariffMasterDetail from './TariffMasterDetail';
import DiscountCategoriesTab from './DiscountCategoriesTab';
import CancellationPeriodsTab from './CancellationPeriodsTab';
import AttendanceCategoriesTab from './AttendanceCategoriesTab';
import { usePermissions } from '@/contexts/PermissionsContext';

/** Top-level tabs (iMikel Fächerverwaltung-Analog). No Fachzuordnungen — tariffs apply to all subjects. */
const MAIN_TABS = [
  { k: 'tariffs', label: 'Tarife', need: 'tariffs' },
  { k: 'discount-categories', label: 'Rabatte Kategorien', need: 'discounts' },
  { k: 'cancellation-periods', label: 'Ausfallperioden', need: 'tariffs' },
  { k: 'attendance-categories', label: 'Anwesenheitsarten', need: 'tariffs' },
];
const READY_TABS = new Set(['tariffs', 'discount-categories', 'cancellation-periods', 'attendance-categories']);
const TARIFF_SECTIONS = [
  { k: 'unterricht', label: 'Unterrichtstarife' },
  { k: 'rental', label: 'Instrumentenmiete' },
];

function resolveMainTab(tabParam, canTariffs, canDiscounts) {
  // Legacy deep-links → Tarife
  if (tabParam === 'discounts' || tabParam === 'rental-tariffs' || tabParam === 'discount-groups') {
    return canTariffs ? 'tariffs' : null;
  }
  const allowed = MAIN_TABS.filter((t) => (t.need === 'tariffs' ? canTariffs : canDiscounts)).map((t) => t.k);
  if (allowed.includes(tabParam)) return tabParam;
  return allowed[0] || 'tariffs';
}

function resolveTariffSection(tabParam, sectionParam) {
  if (tabParam === 'rental-tariffs') return 'rental';
  // Legacy: Einfache Rabatte → Unterrichtstarife
  if (tabParam === 'discounts' || sectionParam === 'discounts') return 'unterricht';
  if (TARIFF_SECTIONS.some((s) => s.k === sectionParam)) return sectionParam;
  return 'unterricht';
}

export default function TariffsDiscounts() {
  const { can } = usePermissions();
  const canTariffs = can('tariffs', 'read');
  const canDiscounts = can('discounts', 'read');
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [zeroOnly, setZeroOnly] = useState(searchParams.get('zeroOnly') === '1');
  const tabParam = searchParams.get('tab');
  const activeTab = resolveMainTab(tabParam, canTariffs, canDiscounts);
  const tariffSection = resolveTariffSection(tabParam, searchParams.get('section'));

  const visibleTabs = useMemo(
    () => MAIN_TABS.filter((t) => (t.need === 'tariffs' ? canTariffs : canDiscounts)),
    [canTariffs, canDiscounts],
  );

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const tariffsRes = await api.get('/tariffs-discounts/tariffs');
      setTariffs(tariffsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const setTab = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'tariffs') {
      next.delete('tab');
      next.delete('section');
    } else {
      next.set('tab', value);
      next.delete('section');
    }
    setSearchParams(next, { replace: true });
  };

  const setTariffSection = (section) => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab'); // stay on Tarife (default)
    if (section === 'unterricht') next.delete('section');
    else next.set('section', section);
    setSearchParams(next, { replace: true });
  };

  return (
    <Layout>
    <div className="min-h-0 flex-1 bg-slate-100 flex flex-col" data-testid="gebuehrenverwaltung-page">
      <div className="bg-white border-b border-slate-200 flex items-center px-4 shrink-0">
        <div className="flex-1 flex items-center overflow-x-auto">
          {visibleTabs.map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-5 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                activeTab === t.k
                  ? 'border-brand-red text-slate-900 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
              {t.k === 'tariffs' && tariffs.length > 0 ? ` (${tariffs.length})` : ''}
            </button>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-1.5 pl-4 text-brand-red font-bold" data-testid="tariffs-title">
          <DollarSign className="w-5 h-5" /> Gebührenverwaltung
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {/* ---------------- TARIFE ---------------- */}
        {activeTab === 'tariffs' && canTariffs && (
          <div className="p-4 md:p-6 space-y-4" data-testid="tab-content-tariffs">
            <p className="text-sm text-slate-600">
              Tarife und Zuschläge gelten fachübergreifend für alle Fächer.
            </p>
            <div className="flex items-center gap-1 border-b border-slate-200">
              {TARIFF_SECTIONS.map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => setTariffSection(s.k)}
                  data-testid={`tariff-section-${s.k}`}
                  className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    tariffSection === s.k
                      ? 'border-slate-800 text-slate-900 font-semibold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {tariffSection === 'unterricht' && (
              <TariffMasterDetail
                tariffs={tariffs}
                loading={loading}
                filter={filter}
                setFilter={setFilter}
                zeroOnly={zeroOnly}
                setZeroOnly={setZeroOnly}
                onReload={loadData}
              />
            )}

            {tariffSection === 'rental' && (
              <div data-testid="tab-rental-tariffs">
                <RentalTariffsTab />
              </div>
            )}
          </div>
        )}

        {/* ---------------- RABATTE KATEGORIEN ---------------- */}
        {activeTab === 'discount-categories' && canDiscounts && (
          <div className="p-4 md:p-6" data-testid="tab-content-discount-categories">
            <DiscountCategoriesTab />
          </div>
        )}

        {/* ---------------- AUSFALLPERIODEN ---------------- */}
        {activeTab === 'cancellation-periods' && canTariffs && (
          <div className="p-4 md:p-6" data-testid="tab-content-cancellation-periods">
            <CancellationPeriodsTab />
          </div>
        )}

        {activeTab === 'attendance-categories' && canTariffs && (
          <div className="p-4 md:p-6" data-testid="tab-content-attendance-categories">
            <AttendanceCategoriesTab />
          </div>
        )}

        {/* ---------------- PLACEHOLDERS ---------------- */}
        {!READY_TABS.has(activeTab) && (
          <div className="h-full min-h-[40vh] flex items-center justify-center" data-testid="gebuehrenverwaltung-placeholder">
            <p className="text-slate-400 text-sm">
              „{MAIN_TABS.find((t) => t.k === activeTab)?.label}“ folgt im nächsten Ausbauschritt.
            </p>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
